import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronDown, ChevronRight, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, ChevronsLeft, ChevronsRight, X, Download, Settings, RotateCcw, Filter, Search as SearchIcon, ArrowUpDown, CheckSquare, Layers, Info } from 'lucide-react';
import SharedModal from '../Modal/SharedModal.jsx';

// ═══ DataTable — Standard GenApps §2.13 + settings modal (gear) + GenAD profile integration ═══
// Prefixo: genapps-table* (conforme IdentidadeVisual §2.13 / regra 08.8)
// Versao componente: 2.0.1
// Features: search global, grouping, pagination, totais, auto-fit headers,
//           selection, loading, density, column visibility, settings (SharedModal),
//           layoutConfig/onLayoutChange (perfil do user no GenAD).
//
// Persistencia (v2.0.0): BINARIA e exclusivamente por perfil de utilizador (GenAD).
//   - layoutConfig = config do perfil (chega no mount ou async; aplicada 1x, sanitizada)
//   - onLayoutChange = emite o payload completo (debounced) em CADA mudanca do user
//   - SEM localStorage, SEM fallbacks: ou o perfil tem config, ou ha defaults da app.

var COL_MIN_WIDTH = 80;
var COL_MAX_WIDTH = 600;

// ─── Header Auto-Fit (regra 08.8 §HEADER LEGIBILITY) ───
// GLOBAL: mede TODOS os headers, encontra o menor font-size necessario,
// aplica esse a TODOS (nunca tamanhos diferentes entre colunas).
var HEADER_FONT_START = 0.875;  // rem
var HEADER_FONT_FLOOR = 0.6;    // rem
var HEADER_FONT_STEP = 0.1;     // rem

function fitAllHeaders(container) {
    if (!container) return;
    var labels = container.querySelectorAll('.genapps-table__th-label');
    if (!labels || labels.length === 0) return;

    // 1. reset todos ao font inicial
    var minFont = HEADER_FONT_START;
    labels.forEach(function (el) {
        el.style.fontSize = HEADER_FONT_START + 'rem';
    });

    // 2. para cada label, calcular o font necessario
    labels.forEach(function (el) {
        var fs = HEADER_FONT_START;
        while (fs > HEADER_FONT_FLOOR && el.scrollWidth > el.clientWidth + 1) {
            fs = +((fs - HEADER_FONT_STEP).toFixed(3));
            if (fs < HEADER_FONT_FLOOR) fs = HEADER_FONT_FLOOR;
            el.style.fontSize = fs + 'rem';
            if (fs <= HEADER_FONT_FLOOR) break;
        }
        if (fs < minFont) minFont = fs;
    });

    // 3. aplicar o minFont a TODOS (uniformidade visual)
    labels.forEach(function (el) {
        el.style.fontSize = minFont + 'rem';
        // ellipsis + tooltip se ainda transborda no minFont
        el.title = (el.scrollWidth > el.clientWidth + 1) ? el.textContent : '';
    });
}

function defaultGetField(row, key) { return row[key] != null ? row[key] : ''; }

function matchesFilter(fieldValue, filterValue) {
    if (!filterValue) return true;
    var val = String(fieldValue || '').trim();
    if (filterValue === '__BLANK__') return val === '' || val === '—';
    // Sugestão do dropdown → match exacto (evita "Inactivo" passar no filtro "Activo" por substring)
    if (filterValue.indexOf('__EQ__:') === 0) {
        return val.toLowerCase() === filterValue.slice(7).toLowerCase();
    }
    return val.toLowerCase().includes(filterValue.toLowerCase());
}

// ─── Normalizacao para search global (ignora acentuacao) ───
function normalizeText(s) {
    return String(s == null ? '' : s)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')   // strip diacriticos
        .toLowerCase()
        .trim();
}

// ─── Agregacao (totais/subtotais) ───
function computeAggregate(rows, col, getField) {
    var fn = col.aggregate;
    var values = rows.map(function (r) { return getField(r, col.key); })
        .filter(function (v) { return v != null && v !== '' && !isNaN(Number(v)); })
        .map(Number);
    if (fn === 'count') {
        // count conta todas as linhas (nao so numericas)
        return rows.length;
    }
    if (!values.length) return null;
    if (fn === 'sum') return values.reduce(function (a, b) { return a + b; }, 0);
    if (fn === 'avg') return values.reduce(function (a, b) { return a + b; }, 0) / values.length;
    if (fn === 'min') return Math.min.apply(null, values);
    if (fn === 'max') return Math.max.apply(null, values);
    return null;
}

function formatAggregate(col, value) {
    if (value == null) return '';
    if (col.totalRender) {
        try { return col.totalRender(value); } catch (e) { return String(value); }
    }
    if (typeof value === 'number') {
        return Number.isInteger(value) ? String(value) : value.toFixed(2);
    }
    return String(value);
}

// ─── Formatadores de celula (v1.8.0) — column.format = 'date'|'datetime'|'eur'|'percent'|'number'|'int'
// Formatacao central e uniforme; col.render (custom) tem sempre prioridade.
var FORMAT_EUR = new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' });
var FORMAT_NUM = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 });
var FORMAT_INT = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 });
function formatCell(col, val) {
    var f = col.format;
    if (!f) return null;                 // sem format -> caller trata (default String)
    if (val == null || val === '') return '—';
    try {
        if (f === 'date') {
            var d = val instanceof Date ? val : new Date(val);
            if (isNaN(d.getTime())) return String(val);
            return d.toLocaleDateString('pt-PT');
        }
        if (f === 'datetime') {
            var dt = val instanceof Date ? val : new Date(val);
            if (isNaN(dt.getTime())) return String(val);
            return dt.toLocaleString('pt-PT');
        }
        if (f === 'eur') return FORMAT_EUR.format(Number(val));
        if (f === 'percent') return FORMAT_NUM.format(Number(val)) + '%';
        if (f === 'int') return FORMAT_INT.format(Number(val));
        if (f === 'number') return FORMAT_NUM.format(Number(val));
    } catch (e) { return String(val); }
    return null;
}

// ─── Sanitizacao do layoutConfig do perfil (v2.0.0) ───
// O perfil GenAD e' dados externos: NUNCA confiar. Tudo o que vier invalido
// (chaves orfas, tipos errados, larguras fora do range, hide-all) e' ignorado.
// O utilizador recebe sempre uma tabela funcional — config corrompida nao parte nada.
function sanitizeLayout(cfg, columns, enableGrouping) {
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return null;
    var byKey = {};
    columns.forEach(function (c) { byKey[c.key] = c; });
    var out = {};
    if (Array.isArray(cfg.colOrder)) {
        var ord = cfg.colOrder.filter(function (k) { return !!byKey[k]; });
        if (ord.length) out.colOrder = ord;
    }
    if (cfg.colWidths && typeof cfg.colWidths === 'object') {
        var w = {};
        Object.keys(cfg.colWidths).forEach(function (k) {
            var v = Number(cfg.colWidths[k]);
            if (byKey[k] && isFinite(v) && v > 0) w[k] = Math.max(COL_MIN_WIDTH, Math.min(COL_MAX_WIDTH, Math.round(v)));
        });
        out.colWidths = w;
    }
    if (cfg.density === 'comfortable' || cfg.density === 'compact') out.density = cfg.density;
    if (Array.isArray(cfg.hiddenColumns)) {
        var hideableCount = columns.filter(function (c) { return c.hideable !== false; }).length;
        var hid = cfg.hiddenColumns.filter(function (k) { return byKey[k] && byKey[k].hideable !== false; });
        // nunca aceitar esconder TODAS as colunas hideable (tabela vazia = confianca perdida)
        if (hid.length > 0 && hid.length < hideableCount) out.hiddenColumns = hid;
    }
    if (typeof cfg.pageSize === 'number' && isFinite(cfg.pageSize) && cfg.pageSize >= 0 && cfg.pageSize <= 1000) {
        out.pageSize = Math.round(cfg.pageSize);
    }
    if (cfg.filters && typeof cfg.filters === 'object') {
        var flt = {};
        Object.keys(cfg.filters).forEach(function (k) {
            if (byKey[k] && byKey[k].filterable !== false) flt[k] = String(cfg.filters[k]);
        });
        out.filters = flt;
    }
    if (enableGrouping && typeof cfg.grouping === 'string' && byKey[cfg.grouping]) out.grouping = cfg.grouping;
    if (cfg.sort && typeof cfg.sort === 'object' && byKey[cfg.sort.key] && byKey[cfg.sort.key].sortable !== false) {
        out.sort = { key: cfg.sort.key, direction: cfg.sort.direction === 'desc' ? 'desc' : 'asc' };
    }
    return out;
}

// ─── SortableHeader ───
function SortableHeader(props) {
    var isActive = props.sortConfig.key === props.fieldKey;
    var canSort = props.sortable !== false;
    var isDragOver = props.dragOverCol === props.fieldKey && props.draggedCol && props.draggedCol !== props.fieldKey;

    return (
        <th
            className={'genapps-table__th' + (isActive ? ' sorted' : '') + (props.draggable ? ' genapps-table__th--draggable' : '') + (!canSort ? ' genapps-table__th--nosort' : '') + (isDragOver ? ' genapps-table__th--drag-over' : '') + (props.pinnedClass || '')}
            onClick={canSort ? function () { props.onSort(props.fieldKey); } : undefined}
            style={Object.assign({}, props.width ? { width: props.width } : null, props.pinnedStyle || null)}
            draggable={props.draggable || false}
            onDragStart={props.onDragStart}
            onDragEnd={props.onDragEnd}
            onDragOver={props.draggable ? function (e) { e.preventDefault(); props.onDragOverCol(props.fieldKey); } : undefined}
            onDragLeave={props.draggable ? function () { props.onDragOverCol(null); } : undefined}
            onDrop={props.draggable ? function (e) { e.preventDefault(); props.onDropCol(props.fieldKey); } : undefined}
        >
            {props.draggable && <span className="genapps-table__th-grip" title="Arraste para reordenar ou agrupar">{'\u2630'}</span>}
            <span className="genapps-table__th-label">{props.headerContent || props.label}</span>
            {canSort && <span className="genapps-table__sort-icon">
                {!isActive && ' \u21C5'}
                {isActive && props.sortConfig.direction === 'asc' && ' \u25B2'}
                {isActive && props.sortConfig.direction === 'desc' && ' \u25BC'}
            </span>}
            <span
                className="genapps-table__resize-handle"
                onMouseDown={function (e) { props.onResizeStart(props.fieldKey, e); }}
                onClick={function (e) { e.stopPropagation(); }}
            />
        </th>
    );
}

// ─── ColumnFilter (typeahead) ───
function ColumnFilter(props) {
    var dropdownRef = useRef(null);
    var _o = useState(false), isOpen = _o[0], setIsOpen = _o[1];
    var _s = useState(''), searchText = _s[0], setSearchText = _s[1];

    useEffect(function () {
        function handleClick(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
        }
        document.addEventListener('mousedown', handleClick);
        return function () { document.removeEventListener('mousedown', handleClick); };
    }, []);

    useEffect(function () {
        if (isOpen) setSearchText(props.filterValue && props.filterValue !== '__BLANK__' ? props.filterValue : '');
        else setSearchText('');
    }, [isOpen]);

    var state = !props.filterValue ? 'all' : props.filterValue === '__BLANK__' ? 'blank' : 'custom';

    function apply(type) {
        if (type === 'all') { props.onFilterChange(''); setSearchText(''); }
        else if (type === 'blank') { props.onFilterChange('__BLANK__'); setSearchText(''); }
        setIsOpen(false);
    }
    function onSearchInput(v) { setSearchText(v); props.onFilterChange(v.trim() || ''); }
    function clear(e) { e.stopPropagation(); props.onFilterChange(''); setSearchText(''); }

    var filteredValues = (props.uniqueValues || []).filter(function (v) {
        if (!searchText.trim()) return true;
        return String(v).toLowerCase().includes(searchText.toLowerCase());
    });
    var getLabel = function () {
        if (state === 'blank') return 'Em Branco';
        if (state === 'custom') return props.filterValue.indexOf('__EQ__:') === 0 ? props.filterValue.slice(7) : props.filterValue;
        return 'Filtrar';
    };

    return (
        <div className="genapps-table__col-filter" ref={dropdownRef}>
            <button className={'genapps-table__col-filter-btn' + (state !== 'all' ? ' active' : '')}
                onClick={function (e) { e.stopPropagation(); setIsOpen(!isOpen); }}>
                <span>{getLabel()}</span>
                {state !== 'all' && <span className="genapps-table__col-filter-clear" onClick={clear}>{'\u2715'}</span>}
            </button>
            {isOpen && (
                <div className="genapps-table__col-filter-dropdown" onClick={function (e) { e.stopPropagation(); }}>
                    <div className="genapps-table__col-filter-option" onClick={function () { apply('all'); }}>
                        <span className={state === 'all' ? 'selected' : ''}>{'\u2713'}</span><span>Todos</span>
                    </div>
                    <div className="genapps-table__col-filter-option" onClick={function () { apply('blank'); }}>
                        <span className={state === 'blank' ? 'selected' : ''}>{'\u2713'}</span><span>Em Branco</span>
                    </div>
                    <div className="genapps-table__col-filter-divider" />
                    <div className="genapps-table__col-filter-search">
                        <input type="text" placeholder="Pesquisar..." value={searchText}
                            onChange={function (e) { onSearchInput(e.target.value); }}
                            onKeyDown={function (e) { if (e.key === 'Enter') setIsOpen(false); }} autoFocus />
                        {searchText ? (
                            <button className="genapps-table__col-filter-clear-input" onClick={clear} title="Limpar">
                                <X size={14} />
                            </button>
                        ) : (
                            <button className="genapps-table__col-filter-ok" onClick={function () { setIsOpen(false); }}>OK</button>
                        )}
                    </div>
                    <div className="genapps-table__col-filter-suggestions">
                        {filteredValues.map(function (v) {
                            return (
                                <div key={String(v)}
                                    className={'genapps-table__col-filter-suggestion' + (state === 'custom' && props.filterValue === '__EQ__:' + String(v) ? ' selected' : '')}
                                    onClick={function () { props.onFilterChange('__EQ__:' + String(v)); setSearchText(''); setIsOpen(false); }}>
                                    {String(v || '—')}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Settings Modal (F11 totais + F14 densidade + F15 colunas + pageSize + clear actions) ───
// Botoes uniformizados com o standard GenApps (.btn-animated-border) — 08.UI_UX_UNIFORME.md §BOTOES
function SettingsModal(props) {
    var isOpen = props.isOpen;
    var onClose = props.onClose;
    var density = props.density;
    var onDensityChange = props.onDensityChange;
    var hideableCols = props.hideableCols;
    var hiddenColumns = props.hiddenColumns;
    var onToggleColumn = props.onToggleColumn;
    var onReset = props.onReset;
    var totalsEnabled = props.totalsEnabled;
    var onToggleTotals = props.onToggleTotals;
    var hasAggregates = props.hasAggregates;
    // pageSize (opcionais)
    var pageSize = props.pageSize;
    var onPageSizeChange = props.onPageSizeChange;
    // clear actions (opcionais)
    var hasSort = props.hasSort;
    var onClearSort = props.onClearSort;
    var hasColFilters = props.hasColFilters;
    var onClearFilters = props.onClearFilters;
    var hasSearch = props.hasSearch;
    var onClearSearch = props.onClearSearch;
    var enableSelection = props.enableSelection;
    var hasSelection = props.hasSelection;
    var onClearSelection = props.onClearSelection;
    var hasGrouping = props.hasGrouping;
    var onClearGrouping = props.onClearGrouping;
    // persistencia por perfil (v2.0.0)
    var persistenceActive = props.persistenceActive;

    // v2.3.1 — steps dinamicos (mesma regra do selector da toolbar): 4 valores a
    // partir do total de linhas, multiplos de 10, mais o pageSize actual e "Todas".
    var totalRows = props.totalRows || 0;
    var pageSizeCurrent = props.pageSize;
    var minRowsOpt = props.minRows || 10;
    var minOpt = Math.max(10, minRowsOpt);
    var totalOpt = Math.max(totalRows, minOpt);
    var stepOpt = Math.max(minOpt, Math.ceil(totalOpt / 4 / 10) * 10);
    var pageOptions = [];
    for (var si = stepOpt; si < totalOpt; si += stepOpt) pageOptions.push(si);
    pageOptions.push(totalOpt);
    if (pageSizeCurrent > 0 && pageOptions.indexOf(pageSizeCurrent) === -1) pageOptions.push(pageSizeCurrent);
    pageOptions = pageOptions.filter(function (v, idx) { return pageOptions.indexOf(v) === idx; })
        .sort(function (a, b) { return a - b; });
    pageOptions.push(0);
    var anyClearAction = hasSort || hasColFilters || hasSearch || (enableSelection && hasSelection) || hasGrouping;

    return (
        <SharedModal
            isOpen={isOpen}
            onClose={onClose}
            title="Configurar Tabela"
            subtitle="Preferências de visualização e estado da tabela"
            icon={<Settings size={20} />}
            size="sm"
            closeOnEscape={true}
            footerContent={
                <>
                    <button type="button" className="btn-animated-border btn-sm btn-secondary"
                        onClick={function () { onReset(); }}>
                        <RotateCcw size={14} /> Repor predefinições
                    </button>
                    <button type="button" className="btn-animated-border btn-sm"
                        data-active="true" onClick={onClose}>
                        <X size={14} /> Fechar
                    </button>
                </>
            }
        >
            {/* Totais */}
            {hasAggregates && (
                <div className="genapps-table__settings-section">
                    <h4 className="genapps-table__settings-title">Linha de Totais</h4>
                    <label className="genapps-table__columns-item">
                        <input type="checkbox" checked={totalsEnabled}
                            onChange={onToggleTotals} />
                        <span>Mostrar linha de totais</span>
                    </label>
                </div>
            )}

            {/* Densidade */}
            <div className="genapps-table__settings-section">
                <h4 className="genapps-table__settings-title">Densidade das Linhas</h4>
                <div className="genapps-table__toggle-row">
                    <span className="genapps-table__toggle-label">Confortável</span>
                    <button type="button" role="switch"
                        aria-checked={density === 'comfortable'}
                        className={'genapps-table__switch' + (density === 'comfortable' ? ' on' : '')}
                        onClick={function () { onDensityChange(density === 'comfortable' ? 'compact' : 'comfortable'); }}
                        title={density === 'comfortable' ? 'Confortável (ligado)' : 'Compacto (desligado)'}>
                        <span className="genapps-table__switch-knob" />
                    </button>
                </div>
            </div>

            {/* Paginacao */}
            <div className="genapps-table__settings-section">
                <h4 className="genapps-table__settings-title">Tamanho da Página</h4>
                <div className="genapps-table__pagesize-toggle">
                    {pageOptions.map(function (opt) {
                        var label = opt === 0 ? 'Todas' : String(opt);
                        return (
                            <button key={opt} type="button"
                                className={'genapps-table__pagesize-btn' + (pageSize === opt ? ' active' : '')}
                                onClick={function () { onPageSizeChange(opt); }}>
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Colunas visiveis */}
            {hideableCols.length > 0 && (
                <div className="genapps-table__settings-section">
                    <h4 className="genapps-table__settings-title">Colunas Visíveis</h4>
                    <div className="genapps-table__columns-list">
                        {hideableCols.map(function (col) {
                            var visible = !hiddenColumns.has(col.key);
                            return (
                                <label key={col.key} className="genapps-table__columns-item">
                                    <input type="checkbox" checked={visible}
                                        onChange={function () { onToggleColumn(col.key); }} />
                                    <span>{col.label}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Limpar estado (opcional — so aparece se houver algo activo) */}
            <div className="genapps-table__settings-section">
                <h4 className="genapps-table__settings-title">Estado da Tabela</h4>
                {anyClearAction ? (
                    <div className="genapps-table__action-list">
                        {hasSort && (
                            <button type="button" className="btn-animated-border btn-sm btn-ghost genapps-table__action-btn"
                                onClick={onClearSort}>
                                <ArrowUpDown size={14} /> Limpar ordenação
                            </button>
                        )}
                        {hasColFilters && (
                            <button type="button" className="btn-animated-border btn-sm btn-ghost genapps-table__action-btn"
                                onClick={onClearFilters}>
                                <Filter size={14} /> Limpar filtros das colunas
                            </button>
                        )}
                        {hasSearch && (
                            <button type="button" className="btn-animated-border btn-sm btn-ghost genapps-table__action-btn"
                                onClick={onClearSearch}>
                                <SearchIcon size={14} /> Limpar pesquisa
                            </button>
                        )}
                        {enableSelection && hasSelection && (
                            <button type="button" className="btn-animated-border btn-sm btn-ghost genapps-table__action-btn"
                                onClick={onClearSelection}>
                                <CheckSquare size={14} /> Limpar seleção
                            </button>
                        )}
                        {hasGrouping && (
                            <button type="button" className="btn-animated-border btn-sm btn-ghost genapps-table__action-btn"
                                onClick={onClearGrouping}>
                                <Layers size={14} /> Remover agrupamento
                            </button>
                        )}
                    </div>
                ) : (
                    <p className="genapps-table__action-empty">Sem ações pendentes — tabela limpa.</p>
                )}
            </div>

            {/* Persistencia por perfil (v2.0.1) — so aparece quando a app integrou o GenAD.
                NUNCA se fala de props/integracao ao utilizador final: isso e' problema do developer. */}
            {persistenceActive && (
                <div className="genapps-table__settings-section">
                    <h4 className="genapps-table__settings-title">Configuração por Perfil</h4>
                    <p className="genapps-table__action-empty">
                        As tuas preferências neste ecrã — filtros, ordenação, agrupamento, colunas,
                        densidade e tamanho de página — são guardadas no teu perfil de utilizador
                        e aplicadas em qualquer dispositivo.
                    </p>
                </div>
            )}
        </SharedModal>
    );
}

// ─── Linha de totais/subtotais ───
function TotalsRow(props) {
    var rows = props.rows;
    var orderedColumns = props.orderedColumns;
    var getField = props.getField;
    var label = props.label;
    var rowClass = props.variant === 'subtotal' ? 'genapps-table__subtotal-row' : 'genapps-table__totals-row';
    var firstColIdx = 0;
    return (
        <tr className={rowClass}>
            {props.renderDetail && <td style={{ width: 36 }}></td>}
            {props.enableSelection && <td style={{ width: 40 }}></td>}
            {orderedColumns.map(function (col, idx) {
                if (col.aggregate) {
                    var val = computeAggregate(rows, col, getField);
                    return (
                        <td key={col.key} className="genapps-table__totals-cell" style={{ width: props.colWidths[col.key] }}>
                            {formatAggregate(col, val)}
                        </td>
                    );
                }
                if (idx === firstColIdx) {
                    return <td key={col.key} style={{ width: props.colWidths[col.key] }}>{label}</td>;
                }
                return <td key={col.key} style={{ width: props.colWidths[col.key] }}></td>;
            })}
        </tr>
    );
}

export function DataTable(props) {
    var columns = props.columns || [];
    var data = props.data || [];
    var getField = props.getField || defaultGetField;
    var rowKey = props.rowKey || function (row, i) { return i; };
    var onRowClick = props.onRowClick;
    var enableGrouping = props.enableGrouping || false;
    var onLayoutChange = props.onLayoutChange;
    // layoutConfig (v2.0.0): configuracao do PERFIL do utilizador (GenAD). Fonte de
    // verdade UNICA — sem fallbacks. Chega no mount ou async (fetch do perfil);
    // quando chega e' aplicada (effect apply-once). null = sem perfil = defaults da app.
    var layoutConfig = props.layoutConfig;
    var enableSelection = props.enableSelection || false;
    var onSelectionChange = props.onSelectionChange;
    var getSelectionControl = props.getSelectionControl;
    var renderDetail = props.renderDetail;   // (row) => ReactNode — se definido, linhas sao expandiveis (drill-down)
    var loading = props.loading || false;
    var initialDensity = props.density || 'comfortable';
    // Min-rows: nº de linhas cuja altura fica sempre reservada. Default 10 via CSS fallback.
    // So injecta a CSS var se a prop vier explicita (nao bloqueia override do host via CSS ancestor).
    var minRowsProp = props.minRows;
    // Empty state: string (emptyText, legacy) OU objecto/node `empty` = { icon, text, action } (v1.8.0)
    var empty = props.empty;

    // Search global: state interno se nao vier controlado via props.globalSearch
    var isControlledSearch = props.globalSearch !== undefined;
    var _gs = useState('');
    var internalSearch = _gs[0], setInternalSearch = _gs[1];
    var globalSearch = isControlledSearch ? props.globalSearch : internalSearch;
    var searchPlaceholder = props.searchPlaceholder || 'Pesquisar em toda a tabela...';
    // colunas incluidas na search global (searchable default true)
    var searchableCols = useMemo(function () {
        return columns.filter(function (c) { return c.searchable !== false; });
    }, [columns]);

    // enableTotals: activo se prop true OU se alguma coluna tiver aggregate
    var hasAggregates = columns.some(function (c) { return !!c.aggregate; });
    var enableTotalsDefault = props.enableTotals || hasAggregates;
    var _te = useState(enableTotalsDefault), totalsEnabled = _te[0], setTotalsEnabled = _te[1];
    var totalsLabel = props.totalsLabel || 'Total';
    var subtotalLabel = props.subtotalLabel || 'Subtotal';

    var groupableCols = columns.filter(function (c) { return c.groupable !== false; });

    // ─── Estado inicial (v2.0.0): derivado do perfil GenAD SANITIZADO ou defaults ───
    // Binario: ou o perfil tem config (layoutConfig) ou a tabela usa os defaults da app.
    // Sem localStorage, sem fallbacks — o mesmo utilizador ve' sempre o mesmo resultado.
    var initCfg = sanitizeLayout(layoutConfig, columns, enableGrouping) || {};

    var initialWidths = {};
    columns.forEach(function (c) {
        var w = c.width || 150;
        if (initCfg.colWidths && initCfg.colWidths[c.key]) {
            w = initCfg.colWidths[c.key];
        }
        initialWidths[c.key] = w;
    });

    var initialColOrder = initCfg.colOrder || null;
    var initialView = { filters: initCfg.filters || {}, grouping: initCfg.grouping || null, sort: initCfg.sort || null };

    var _sort = useState(initialView.sort || props.initialSort || { key: null, direction: 'asc' });
    var sortConfig = _sort[0], setSortConfig = _sort[1];
    var _filt = useState(initialView.filters);
    var colFilters = _filt[0], setColFilters = _filt[1];
    var _w = useState(initialWidths);
    var colWidths = _w[0], setColWidths = _w[1];
    var _g = useState(initialView.grouping);
    var groupKey = _g[0], setGroupKey = _g[1];
    var _collapsed = useState(new Set());
    var collapsedGroups = _collapsed[0], setCollapsedGroups = _collapsed[1];
    var _page = useState(0);
    var currentPage = _page[0], setCurrentPage = _page[1];
    var persistedPageSize = initCfg.pageSize !== undefined ? initCfg.pageSize : (props.pageSize !== undefined ? props.pageSize : 20);
    var _ps = useState(persistedPageSize);
    var pageSize = _ps[0], setPageSize = _ps[1];
    var _drag = useState(null), draggedCol = _drag[0], setDraggedCol = _drag[1];
    var _over = useState(false), isDropOver = _over[0], setIsDropOver = _over[1];
    var _dCol = useState(null), dragOverCol = _dCol[0], setDragOverCol = _dCol[1];
    var _colOrder = useState(initialColOrder), colOrder = _colOrder[0], setColOrder = _colOrder[1];
    // versao do layout -> dispara re-fit dos headers quando muda
    var _lv = useState(0), layoutVersion = _lv[0], bumpLayout = _lv[1];

    // ─── Seleção de linhas (F12) ───
    var _sel = useState(new Set()), selectedKeys = _sel[0], setSelectedKeys = _sel[1];

    // ─── Drill-down (v1.8.0 → v1.8.1): detalhe SEMPRE via SharedModal (R11) ───
    // O chevron lider abre um SharedModal com renderDetail(row). NAO ha expansao inline.
    var detailTitle = props.detailTitle;       // (row) => string | string
    var detailSubtitle = props.detailSubtitle; // (row) => string | string
    var detailSize = props.detailSize || 'md';
    var _dr = useState(null), detailRow = _dr[0], setDetailRow = _dr[1];
    // Nº de celulas "leader" antes das colunas (checkbox + drill-down chevron)
    var leadCols = (enableSelection ? 1 : 0) + (renderDetail ? 1 : 0);

    // ─── Densidade (F14) — do perfil ou default ───
    var _dens = useState(initCfg.density || initialDensity), density = _dens[0], setDensity = _dens[1];

    // ─── Column visibility (F15) — do perfil ou default ───
    var hideableCols = columns.filter(function (c) { return c.hideable !== false; });
    var _hid = useState(new Set(initCfg.hiddenColumns || [])), hiddenColumns = _hid[0], setHiddenColumns = _hid[1];

    // ─── Settings modal (gear) ───
    var _sm = useState(false), settingsOpen = _sm[0], setSettingsOpen = _sm[1];
    var tableRef = useRef(null);
    var warnedNoPersist = useRef(false);

    // v2.0.1: persistencia por perfil (GenAD) e' OBRIGATORIA nas GenApps. Se a app
    // nao integrou onLayoutChange, o aviso vai para a CONSOLA DO DEVELOPER (1x por
    // instancia) — o utilizador final NUNCA ve linguagem de integracao na UI.
    if (!onLayoutChange && !warnedNoPersist.current) {
        warnedNoPersist.current = true;
        if (typeof console !== 'undefined' && console.warn) {
            console.warn('[genapps-shared-ui/DataTable] Esta app nao integrou a persistencia por perfil (GenAD): falta onLayoutChange. Integrar com useTableProfile — ver MANUAIS/08.8.TABLES.md.');
        }
    }

    // ─── Reset aos defaults da APP (layout + view state) ───
    var resetLayout = useCallback(function () {
        var defaultWidths = {};
        columns.forEach(function (c) { defaultWidths[c.key] = c.width || 150; });
        setColOrder(null);
        setColWidths(defaultWidths);
        setHiddenColumns(new Set());
        setDensity(initialDensity);
        setPageSize(props.pageSize !== undefined ? props.pageSize : 20);
        setCurrentPage(0);
        setSortConfig(props.initialSort || { key: null, direction: 'asc' });
        setColFilters({});
        setGroupKey(null);
        setCollapsedGroups(new Set());
        bumpLayout(function (v) { return v + 1; });
        // o emit effect (abaixo) entrega o payload de defaults ao GenAD via onLayoutChange
    }, [columns, initialDensity, props.pageSize, props.initialSort]);

    // ═══ Persistencia v2.0.0 — GenAD exclusivo, binario, zero fallbacks ═══
    //
    // 1. APPLY-ONCE: quando o layoutConfig (perfil GenAD) chega — no mount ou async
    //    via fetch — e' sanitizado e aplicado UMA vez. Chegada tardia nao pode ser
    //    ignorada (era a causa de "gravo e ao dia seguinte esta' tudo ao default").
    // 2. EMIT (debounced 400ms): QUALQUER mudanca do utilizador (filtros, sort,
    //    grouping, colunas, densidade, pageSize, larguras, reorder, reset) emite o
    //    payload COMPLETO via onLayoutChange — a app grava no perfil do utilizador.
    //    Um unico caminho de gravacao = sem races, sem estados fantasma.
    // 3. Nada de localStorage: o perfil e' do UTILIZADOR, nao do browser. Em browser
    //    partilhado, localStorage vaza config entre users = confianca perdida.
    var appliedProfile = useRef(false);
    var emitMounted = useRef(false);
    var userInteracted = useRef(false);
    var lastEmittedJson = useRef(null);
    var emitTimer = useRef(null);

    function buildPayload(o, w, d, h, ps, f, g, s) {
        return {
            colOrder: o, colWidths: w, density: d,
            hiddenColumns: Array.from(h), pageSize: ps,
            filters: f, grouping: g,
            sort: (s && s.key) ? s : null
        };
    }

    // (1) aplicar o perfil quando chega.
    // v2.3.0 — PROTECCAO DE INTERACCAO: se o utilizador ja' mexeu na tabela antes de o
    // perfil chegar (GET lento), o perfil e' DESCARTADO — nunca atropela o user. Era a
    // causa de "mudo 50, grava 50, e um segundo depois grava 25 (perfil velho) por cima".
    useEffect(function () {
        if (appliedProfile.current) return;
        if (!layoutConfig || typeof layoutConfig !== 'object') return;
        if (userInteracted.current) { appliedProfile.current = true; return; }
        appliedProfile.current = true;
        var cfg = sanitizeLayout(layoutConfig, columns, enableGrouping) || {};
        var nextOrder = cfg.colOrder || colOrder || columns.map(function (c) { return c.key; });
        var nextWidths = Object.assign({}, colWidths, cfg.colWidths);
        var nextDensity = cfg.density || density;
        var nextHidden = cfg.hiddenColumns ? new Set(cfg.hiddenColumns) : hiddenColumns;
        var nextPageSize = cfg.pageSize !== undefined ? cfg.pageSize : pageSize;
        var nextFilters = Object.assign({}, colFilters, cfg.filters);
        var nextGrouping = cfg.grouping !== undefined ? cfg.grouping : groupKey;
        var nextSort = cfg.sort || sortConfig;
        // bloquear eco: o emit effect nao deve re-gravar no GenAD o que acabou de carregar
        lastEmittedJson.current = JSON.stringify(buildPayload(nextOrder, nextWidths, nextDensity, nextHidden, nextPageSize, nextFilters, nextGrouping, nextSort));
        if (emitTimer.current) { clearTimeout(emitTimer.current); emitTimer.current = null; }
        setColOrder(nextOrder);
        setColWidths(nextWidths);
        setDensity(nextDensity);
        setHiddenColumns(nextHidden);
        setPageSize(nextPageSize);
        setColFilters(nextFilters);
        setGroupKey(nextGrouping);
        setSortConfig(nextSort || { key: null, direction: 'asc' });
        setCurrentPage(0);
        bumpLayout(function (v) { return v + 1; });
    }, [layoutConfig]);

    // (2) emitir mudancas do utilizador (debounced — resize nao faz flood ao backend)
    useEffect(function () {
        var json = JSON.stringify(buildPayload(
            colOrder || columns.map(function (c) { return c.key; }),
            colWidths, density, hiddenColumns, pageSize, colFilters, groupKey, sortConfig
        ));
        if (!emitMounted.current) { emitMounted.current = true; lastEmittedJson.current = json; return; }
        if (!onLayoutChange) return;
        if (json === lastEmittedJson.current) return;
        // v2.3.0: o utilizador mexeu no estado (nao e' eco do mount) — a partir
        // daqui, um perfil que chegue tarde NUNCA pode atropelar esta vontade.
        userInteracted.current = true;
        if (emitTimer.current) clearTimeout(emitTimer.current);
        emitTimer.current = setTimeout(function () {
            emitTimer.current = null;
            lastEmittedJson.current = json;
            // diagnostico (v2.2.1): visivel na consola do browser — se o POST nao
            // chega ao GenAD, a causa esta' DEPOIS desta linha (rede/token/hook)
            if (typeof console !== 'undefined' && console.info) {
                console.info('[DataTable] a gravar perfil:', JSON.parse(json).pageSize !== undefined ? 'pageSize=' + JSON.parse(json).pageSize : '', '(payload ' + json.length + 'B)');
            }
            try { onLayoutChange(JSON.parse(json)); } catch (e) {
                if (typeof console !== 'undefined' && console.warn) console.warn('[DataTable] onLayoutChange lancou erro (perfil NAO gravado):', e && e.message);
            }
        }, 400);
        return function () { if (emitTimer.current) { clearTimeout(emitTimer.current); emitTimer.current = null; } };
    }, [colOrder, colWidths, density, hiddenColumns, pageSize, colFilters, groupKey, sortConfig, onLayoutChange]);

    // ─── Toggle densidade ───
    var handleDensityChange = useCallback(function (d) {
        setDensity(d);
    }, []);

    // ─── Toggle column visibility ───
    var toggleColumnVisibility = useCallback(function (colKey) {
        setHiddenColumns(function (prev) {
            var next = new Set(prev);
            // minimo 1 coluna visivel (entre as hideable)
            if (next.has(colKey)) {
                next.delete(colKey);
            } else {
                // nao permitir esconder se for a ultima visivel
                var visibleCount = hideableCols.filter(function (c) { return !next.has(c.key); }).length;
                if (visibleCount <= 1) return prev; // manter
                next.add(colKey);
            }
            bumpLayout(function (v) { return v + 1; });
            return next;
        });
    }, [hideableCols]);

    // ─── Tamanho da página (opcional do Settings) ───
    var handlePageSizeChange = useCallback(function (v) {
        setPageSize(v);
        setCurrentPage(0);
    }, []);

    // ─── Clear actions (opcionais do Settings) ───
    var handleClearSort = useCallback(function () {
        setSortConfig({ key: null, direction: 'asc' });
    }, []);
    var handleClearFilters = useCallback(function () {
        setColFilters({});
        setCurrentPage(0);
    }, []);
    var handleClearSearch = useCallback(function () {
        if (!isControlledSearch) setInternalSearch('');
        if (props.onSearchChange) props.onSearchChange('');
        setCurrentPage(0);
    }, [isControlledSearch, props]);
    var handleClearSelection = useCallback(function () {
        setSelectedKeys(new Set());
        if (onSelectionChange) onSelectionChange([], []);
    }, [onSelectionChange]);
    var handleClearGrouping = useCallback(function () {
        setGroupKey(null);
        setCollapsedGroups(new Set());
        setCurrentPage(0);
    }, []);

    // ─── Sort ───
    var handleSort = useCallback(function (key) {
        setSortConfig(function (prev) {
            return { key: key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' };
        });
    }, []);

    // ─── Resize ───
    var handleResizeStart = useCallback(function (colKey, e) {
        e.preventDefault(); e.stopPropagation();
        var startX = e.clientX;
        var startWidth = colWidths[colKey] || COL_MIN_WIDTH;
        function onMove(ev) {
            var next = Math.max(COL_MIN_WIDTH, Math.min(COL_MAX_WIDTH, startWidth + (ev.clientX - startX)));
            setColWidths(function (prev) { var c = Object.assign({}, prev); c[colKey] = next; return c; });
        }
        function onUp() {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.cursor = ''; document.body.style.userSelect = '';
            // larguras finais: o emit effect (debounced) entrega-as ao GenAD
        }
        document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [colWidths]);

    var setFilter = useCallback(function (colKey, value) {
        setColFilters(function (prev) { var n = Object.assign({}, prev); if (value) n[colKey] = value; else delete n[colKey]; return n; });
        setCurrentPage(0);
    }, []);

    // ─── Column Reorder (drag-and-drop de headers) ───
    var orderedColumns = useMemo(function () {
        if (!colOrder) return columns;
        var map = {};
        columns.forEach(function (c) { map[c.key] = c; });
        var result = colOrder.map(function (k) { return map[k]; }).filter(Boolean);
        var missing = columns.filter(function (c) { return !colOrder.includes(c.key); });
        return result.concat(missing);
    }, [columns, colOrder]);

    // Colunas visiveis (orderedColumns menos as escondidas via F15)
    var visibleColumns = useMemo(function () {
        return orderedColumns.filter(function (c) { return !hiddenColumns.has(c.key); });
    }, [orderedColumns, hiddenColumns]);

    // ─── Auto-fit global dos headers (regra 08.8 §HEADER LEGIBILITY) ───
    // Mede TODOS os labels, aplica o menor font-size necessario a TODOS (uniforme).
    // (v1.8.0) movido para DEPOIS de visibleColumns — antes referenciava-o antes do useMemo.
    useLayoutEffect(function () {
        fitAllHeaders(tableRef.current);
    }, [visibleColumns, colWidths, layoutVersion]);

    // ─── Column pinning (v1.8.0) — col.pin: 'left' congela a coluna (sticky left) ───
    var pinned = useMemo(function () {
        var offsets = {};
        var acc = 0;
        var lastKey = null;
        var any = false;
        visibleColumns.forEach(function (col) {
            if (col.pin === 'left') {
                offsets[col.key] = acc;
                acc += colWidths[col.key] || col.width || 150;
                lastKey = col.key;
                any = true;
            }
        });
        return { offsets: offsets, lastKey: lastKey, any: any };
    }, [visibleColumns, colWidths]);
    function pinStyle(col, zIndex) {
        if (col.pin !== 'left') return null;
        return { position: 'sticky', left: (pinned.offsets[col.key] || 0), zIndex: zIndex };
    }
    function pinClass(col) {
        if (col.pin !== 'left') return '';
        return ' genapps-table__pin' + (col.key === pinned.lastKey ? ' genapps-table__pin--last' : '');
    }

    var handleDropCol = useCallback(function (targetKey) {
        if (!draggedCol || draggedCol === targetKey) { setDraggedCol(null); setDragOverCol(null); return; }
        var order = (colOrder || columns.map(function (c) { return c.key; })).slice();
        var fromIdx = order.indexOf(draggedCol);
        var toIdx = order.indexOf(targetKey);
        if (fromIdx === -1 || toIdx === -1) { setDraggedCol(null); setDragOverCol(null); return; }
        order.splice(fromIdx, 1);
        order.splice(toIdx, 0, draggedCol);
        setColOrder(order);
        setDraggedCol(null);
        setDragOverCol(null);
        bumpLayout(function (v) { return v + 1; });
    }, [draggedCol, colOrder, columns]);

    function toggleGroup(val) {
        setCollapsedGroups(function (prev) {
            var next = new Set(prev);
            if (next.has(val)) next.delete(val); else next.add(val);
            return next;
        });
    }

    // ─── Seleção de linhas (F12) ───
    function notifySelectionChange(nextSet) {
        if (onSelectionChange) {
            var selectedRows = [];
            var keyToRow = {};
            filtered.forEach(function (r) { keyToRow[rowKey(r)] = r; });
            nextSet.forEach(function (k) { if (keyToRow[k]) selectedRows.push(keyToRow[k]); });
            onSelectionChange(selectedRows, Array.from(nextSet));
        }
    }
    function toggleRowSelection(row) {
        var k = rowKey(row);
        setSelectedKeys(function (prev) {
            var next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            notifySelectionChange(next);
            return next;
        });
    }
    function toggleSelectAllVisible() {
        var visibleRowKeys = displayRows.map(function (r) { return rowKey(r); });
        var allSelected = visibleRowKeys.every(function (k) { return selectedKeys.has(k); });
        setSelectedKeys(function (prev) {
            var next = new Set(prev);
            if (allSelected) {
                visibleRowKeys.forEach(function (k) { next.delete(k); });
            } else {
                visibleRowKeys.forEach(function (k) { next.add(k); });
            }
            notifySelectionChange(next);
            return next;
        });
    }

    // ─── Column unique values (for filter suggestions) ───
    var colValues = useMemo(function () {
        var result = {};
        columns.forEach(function (col) {
            var set = new Set();
            data.forEach(function (row) {
                var v = getField(row, col.key);
                if (v != null && v !== '' && v !== '—') set.add(String(v));
            });
            result[col.key] = Array.from(set).sort();
        });
        return result;
    }, [data, columns, getField]);

    // ─── Filter + Sort + Search global ───
    var filtered = useMemo(function () {
        var f = data;
        if (Object.keys(colFilters).length > 0) {
            f = f.filter(function (row) {
                return Object.keys(colFilters).every(function (col) {
                    return matchesFilter(getField(row, col), colFilters[col]);
                });
            });
        }
        // Search global: ignora acentuacao, procura em todas as colunas searchable (AND com colFilters)
        var normTerm = normalizeText(globalSearch);
        if (normTerm) {
            // Multi-palavra (AND): 'joao lucas' encontra 'João Manuel Lucas da Silva'
            // — cada palavra tem de constar no texto pesquisável da linha (concat
            // das colunas searchable; \u0001 evita matches cruzados entre colunas).
            var words = normTerm.split(/\s+/).filter(Boolean);
            f = f.filter(function (row) {
                var rowText = searchableCols.map(function (col) {
                    return normalizeText(getField(row, col.key));
                }).join(' \u0001 ');
                return words.every(function (w) { return rowText.indexOf(w) !== -1; });
            });
        }
        if (sortConfig.key) {
            var dir = sortConfig.direction === 'asc' ? 1 : -1;
            var key = sortConfig.key;
            f = [].concat(f).sort(function (a, b) {
                var va = getField(a, key), vb = getField(b, key);
                if (va == null && vb == null) return 0;
                if (va == null) return 1;
                if (vb == null) return -1;
                if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
                return String(va).localeCompare(String(vb), 'pt', { numeric: true }) * dir;
            });
        }
        return f;
    }, [data, colFilters, sortConfig, getField, globalSearch, searchableCols]);

    // ─── Group ───
    var groups = useMemo(function () {
        if (!groupKey) return null;
        var map = new Map();
        filtered.forEach(function (row) {
            var val = String(getField(row, groupKey) || '—');
            if (!map.has(val)) map.set(val, []);
            map.get(val).push(row);
        });
        return Array.from(map.entries()).map(function (entry) {
            return { value: entry[0], rows: entry[1] };
        }).sort(function (a, b) { return a.value.localeCompare(b.value, 'pt', { numeric: true }); });
    }, [filtered, groupKey, getField]);

    // ─── Pagination ───
    var pagedData = useMemo(function () {
        if (groups) return null; // pagination not applied when grouping
        if (!pageSize || pageSize <= 0) return filtered;
        var start = currentPage * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, groups, pageSize, currentPage]);

    var totalPages = pageSize > 0 && !groups ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;

    function getPageNumbers() {
        var pages = [], maxVisible = 5;
        var start = Math.max(1, currentPage + 1 - Math.floor(maxVisible / 2));
        var end = Math.min(totalPages, start + maxVisible - 1);
        start = Math.max(1, end - maxVisible + 1);
        if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
        for (var i = start; i <= end; i++) pages.push(i);
        if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
        return pages;
    }

    function renderCellText(row, col) {
        var val = getField(row, col.key);
        if (val == null || val === '') return '—';
        if (col.format) {
            var fmt = formatCell(col, val);
            if (fmt !== null) return fmt;
        }
        return String(val);
    }

    function renderCell(row, col) {
        if (col.render) return col.render(row);
        return renderCellText(row, col);
    }

    function renderCheckbox(row) {
        if (!enableSelection) return null;
        // getSelectionControl permite esconder o checkbox numa linha específica
        if (getSelectionControl && getSelectionControl(row) === false) {
            return <td className="genapps-table__checkbox-cell" style={{ width: 40 }}></td>;
        }
        var k = rowKey(row);
        var checked = selectedKeys.has(k);
        return (
            <td className="genapps-table__checkbox-cell" style={{ width: 40 }} onClick={function (e) { e.stopPropagation(); }}>
                <input type="checkbox" className="genapps-table__checkbox" checked={checked}
                    onChange={function () { toggleRowSelection(row); }} />
            </td>
        );
    }

    // Celula leader do drill-down (v1.8.0): chevron abre SharedModal de detalhe (R11)
    function renderExpandToggle(row) {
        if (!renderDetail) return null;
        return (
            <td className="genapps-table__expand-cell" style={{ width: 36 }} onClick={function (e) { e.stopPropagation(); }}>
                <button type="button" className="genapps-table__expand-toggle"
                    aria-label="Ver detalhe"
                    title="Ver detalhe"
                    onClick={function () { setDetailRow(row); }}>
                    <ChevronRight size={14} />
                </button>
            </td>
        );
    }

    function renderRows(rowsToRender) {
        return rowsToRender.map(function (row, i) {
            var key = rowKey(row, i);
            var isSelected = enableSelection && selectedKeys.has(key);
            return (
                <tr key={key} className={'genapps-table__row' + (onRowClick ? ' genapps-table__row--clickable' : '') + (isSelected ? ' genapps-table__row--selected' : '') + (props.rowClassName ? ' ' + props.rowClassName(row) : '')}
                    onClick={onRowClick ? function (e) {
                        if (e.target.closest('.genapps-table__expand-toggle') || e.target.closest('.genapps-table__actions') || e.target.closest('.genapps-table__cell-actions') || e.target.closest('button') || e.target.closest('a') || e.target.closest('input[type="checkbox"]')) return;
                        onRowClick(row);
                    } : undefined}>
                    {renderExpandToggle(row)}
                    {renderCheckbox(row)}
                    {visibleColumns.map(function (col) {
                        var cellText = col.render ? '' : renderCellText(row, col);
                        return <td key={col.key} className={(col.cellClassName || '') + pinClass(col)} style={Object.assign({ width: colWidths[col.key] }, pinStyle(col, 6) || {})} title={cellText && cellText !== '—' ? cellText : undefined}>{renderCell(row, col)}</td>;
                    })}
                </tr>
            );
        });
    }

    function renderSkeletonRows() {
        var count = (pageSize && pageSize > 0) ? Math.min(pageSize, 8) : 8;
        var rows = [];
        for (var i = 0; i < count; i++) {
            rows.push(
                <tr key={'sk-' + i} className="genapps-table__skeleton-row">
                    {renderDetail && <td className="genapps-table__skeleton-cell" style={{ width: 36 }}><span className="genapps-table__skeleton-bar" style={{ width: 12, height: 12 }}></span></td>}
                    {enableSelection && <td className="genapps-table__skeleton-cell" style={{ width: 40 }}><span className="genapps-table__skeleton-bar" style={{ width: 16, height: 16 }}></span></td>}
                    {visibleColumns.map(function (col) {
                        return (
                            <td key={col.key} className="genapps-table__skeleton-cell" style={{ width: colWidths[col.key] }}>
                                <span className="genapps-table__skeleton-bar" style={{ width: (50 + (i * 7) % 40) + '%' }}></span>
                            </td>
                        );
                    })}
                </tr>
            );
        }
        return rows;
    }

    var displayRows = groups ? null : (pagedData || filtered);
    var totalShown = filtered.length;

    // ─── Estado activo (para habilitar clear actions no Settings) ───
    var hasSort = !!(sortConfig && sortConfig.key);
    var hasColFilters = !!(colFilters && Object.keys(colFilters).length > 0);
    var hasSearch = !!(globalSearch && String(globalSearch).trim());
    var hasSelection = enableSelection && selectedKeys.size > 0;
    var hasGrouping = !!groupKey;

    // ─── Totais: linhas baseadas nos dados FILTRADOS (nao paginados) ───
    var totalsRowsForFooter = totalsEnabled ? filtered : null;

    async function exportarXLSX() {
        var rowsToExport = groups ? filtered : filtered;
        var XLSX = await import('xlsx');
        var wb = XLSX.utils.book_new();
        var wsData = [visibleColumns.map(function (c) { return c.label; })];
        rowsToExport.forEach(function (row) {
            wsData.push(visibleColumns.map(function (col) {
                var val = getField(row, col.key);
                return val != null && val !== '' ? String(val) : '';
            }));
        });
        // incluir linha de totais no export se activo
        if (totalsEnabled) {
            var totalRow = visibleColumns.map(function (col, idx) {
                if (col.aggregate) return formatAggregate(col, computeAggregate(filtered, col, getField));
                if (idx === 0) return totalsLabel;
                return '';
            });
            wsData.push(totalRow);
        }
        var ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = visibleColumns.map(function (c) { return { wch: Math.max(c.label.length + 2, 15) }; });
        XLSX.utils.book_append_sheet(wb, ws, 'Dados');
        var d = new Date();
        var pad = function (n) { return String(n).padStart(2, '0'); };
        var ts = pad(d.getDate()) + pad(d.getMonth() + 1) + d.getFullYear() + pad(d.getHours()) + pad(d.getMinutes());
        XLSX.writeFile(wb, (props.exportPrefix || 'genapps') + '_export_' + ts + '.xlsx');
    }

    return (
        <div className={'genapps-table-wrapper' + (density === 'compact' ? ' genapps-table-wrapper--compact' : '')}
             style={minRowsProp !== undefined ? { '--genapps-table-min-rows': minRowsProp } : undefined}>
            <div className="genapps-table__toolbar">
                    <div className="genapps-table__toolbar-left">
                        {/* Search global (OBRIGATORIA — ignora acentuacao, tempo real) */}
                        <div className="genapps-table__search">
                            <span className="genapps-table__search-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            </span>
                            <input
                                type="text"
                                className="genapps-table__search-input"
                                placeholder={searchPlaceholder}
                                value={globalSearch || ''}
                                onChange={function (e) {
                                    var v = e.target.value;
                                    if (!isControlledSearch) setInternalSearch(v);
                                    if (props.onSearchChange) props.onSearchChange(v);
                                    setCurrentPage(0);
                                }}
                            />
                            {globalSearch ? (
                                <button className="genapps-table__search-clear" title="Limpar pesquisa"
                                    onClick={function () {
                                        if (!isControlledSearch) setInternalSearch('');
                                        if (props.onSearchChange) props.onSearchChange('');
                                        setCurrentPage(0);
                                    }}>
                                    <X size={14} />
                                </button>
                            ) : null}
                        </div>
                        {enableGrouping && (
                            groupKey ? (
                                <span className="genapps-table__group-chip"
                                    onDragOver={function (e) { e.preventDefault(); setIsDropOver(true); }}
                                    onDragLeave={function () { setIsDropOver(false); }}
                                    onDrop={function (e) { e.preventDefault(); setIsDropOver(false); if (draggedCol) { setGroupKey(draggedCol); setDraggedCol(null); } }}>
                                    <span className="genapps-table__group-chip-label">{'Agrupado: ' + (columns.find(function (c) { return c.key === groupKey; }) || {}).label}</span>
                                    <span className="genapps-table__group-chip-count">{totalShown}</span>
                                    <button className="genapps-table__group-chip-remove" onClick={function () { setGroupKey(null); setCollapsedGroups(new Set()); setCurrentPage(0); }}>
                                        <X size={12} />
                                    </button>
                                </span>
                            ) : (
                                <span className={'genapps-table__group-dropzone' + (isDropOver ? ' drag-over' : '')}
                                    onDragOver={function (e) { e.preventDefault(); setIsDropOver(true); }}
                                    onDragLeave={function () { setIsDropOver(false); }}
                                    onDrop={function (e) { e.preventDefault(); setIsDropOver(false); if (draggedCol) { setGroupKey(draggedCol); setDraggedCol(null); } }}>
                                    {'⠿ Arraste uma coluna para agrupar'}
                                </span>
                            )
                        )}
                        <button className="btn-animated-border btn-sm" onClick={exportarXLSX} title="Exportar para XLSX">
                            <Download size={14} /> XLSX
                        </button>
                        {enableSelection && selectedKeys.size > 0 && (
                            <span className="genapps-table__selection-count">{selectedKeys.size} selecionada{selectedKeys.size > 1 ? 's' : ''}</span>
                        )}
                    </div>
                    <div className="genapps-table__toolbar-actions">
                        {/* Settings (gear) — abre SharedModal com densidade + colunas + reset */}
                        <button className="genapps-table__settings-btn" onClick={function () { setSettingsOpen(true); }}
                            title="Configurar tabela (densidade, colunas visíveis)">
                            <Settings size={16} />
                        </button>
                        {pageSize > 0 && (
                        <>
                        <span className="genapps-table__toolbar-info">
                            {totalShown > 0 ? (currentPage * pageSize + 1) : 0}–{Math.min((currentPage + 1) * pageSize, totalShown)} de {totalShown}
                        </span>
                        <select className="genapps-table__page-size-select" value={pageSize} onChange={function (e) { setPageSize(parseInt(e.target.value)); setCurrentPage(0); }}>
                            {/* v2.3.1 — steps DINAMICOS a partir do total de linhas visiveis (totalShown).
                                4 valores max, multiplos de 10 (piso minRows=10), nunca acima do total:
                                314 linhas -> 80/160/240/310 | 45 -> 20/40/45 | <=10 -> so o proprio.
                                O pageSize actual do perfil entra SEMPRE na lista (sem valor mentiroso). */}
                            {(function () {
                                var min = Math.max(10, minRowsProp || 10);
                                var total = Math.max(totalShown, min);
                                var step = Math.max(min, Math.ceil(total / 4 / 10) * 10);
                                var opts = [];
                                for (var i = step; i < total; i += step) opts.push(i);
                                opts.push(total);
                                if (pageSize > 0 && opts.indexOf(pageSize) === -1) opts.push(pageSize);
                                opts = opts.filter(function (v, idx) { return opts.indexOf(v) === idx; })
                                    .sort(function (a, b) { return a - b; });
                                return opts.map(function (n) {
                                    return <option key={n} value={n}>{n} / pág</option>;
                                });
                            })()}
                            <option value={0}>Todas</option>
                        </select>
                        <button className="genapps-table__nav-btn" disabled={currentPage === 0} onClick={function () { setCurrentPage(0); }} title="Primeira página">
                            <ChevronsLeft size={16} />
                        </button>
                        <button className="genapps-table__nav-btn" disabled={currentPage === 0} onClick={function () { setCurrentPage(currentPage - 1); }} title="Página anterior">
                            <ChevronLeftIcon size={16} />
                        </button>
                        <span className="genapps-table__nav-current">{currentPage + 1}/{totalPages}</span>
                        <button className="genapps-table__nav-btn" disabled={currentPage >= totalPages - 1} onClick={function () { setCurrentPage(currentPage + 1); }} title="Página seguinte">
                            <ChevronRightIcon size={16} />
                        </button>
                        <button className="genapps-table__nav-btn" disabled={currentPage >= totalPages - 1} onClick={function () { setCurrentPage(totalPages - 1); }} title="Última página">
                            <ChevronsRight size={16} />
                        </button>
                        </>
                        )}
                    </div>
                </div>

            <div className="genapps-table-container">
                <table ref={tableRef} className={'genapps-table' + (density === 'compact' ? ' genapps-table--compact' : '')}>
                    <thead>
                        <tr>
                            {renderDetail && <th className="genapps-table__th-expand" style={{ width: 36 }}></th>}
                            {enableSelection && (
                                <th className="genapps-table__th-checkbox" style={{ width: 40 }}>
                                    <input type="checkbox" className="genapps-table__checkbox"
                                        checked={displayRows && displayRows.length > 0 && displayRows.every(function (r) { return selectedKeys.has(rowKey(r)); })}
                                        ref={function (el) { if (el) el.indeterminate = displayRows && !displayRows.every(function (r) { return selectedKeys.has(rowKey(r)); }) && displayRows.some(function (r) { return selectedKeys.has(rowKey(r)); }); }}
                                        onChange={toggleSelectAllVisible} />
                                </th>
                            )}
                            {visibleColumns.map(function (col) {
                                return (
                                    <SortableHeader key={col.key} fieldKey={col.key} label={col.label}
                                        headerContent={col.headerContent}
                                        sortable={col.sortable !== false}
                                        sortConfig={sortConfig} onSort={handleSort}
                                        width={colWidths[col.key]} onResizeStart={handleResizeStart}
                                        pinnedStyle={pinStyle(col, 31)}
                                        pinnedClass={pinClass(col)}
                                        draggable={enableGrouping || props.enableColumnReorder !== false}
                                        draggedCol={draggedCol} dragOverCol={dragOverCol}
                                        onDragOverCol={setDragOverCol} onDropCol={handleDropCol}
                                        colOrderVersion={layoutVersion}
                                        onDragStart={function () { setDraggedCol(col.key); }}
                                        onDragEnd={function () { setDraggedCol(null); setDragOverCol(null); }} />
                                );
                            })}
                        </tr>
                        <tr className="genapps-table__filter-row">
                            {renderDetail && <th style={{ width: 36 }}></th>}
                            {enableSelection && <th style={{ width: 40 }}></th>}
                            {visibleColumns.map(function (col) {
                                return (
                                    <th key={col.key} className={pinClass(col).trim() || undefined} style={Object.assign({ width: colWidths[col.key] }, pinStyle(col, 21) || {})}>
                                        {col.filterable !== false ? <ColumnFilter filterValue={colFilters[col.key] || ''}
                                            onFilterChange={function (v) { setFilter(col.key, v); }}
                                            uniqueValues={colValues[col.key]} /> : null}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? renderSkeletonRows() : (
                            <>
                                {totalShown === 0 && (
                                    <tr><td colSpan={visibleColumns.length + leadCols} className="genapps-table__empty">
                                        {empty && typeof empty === 'object' && empty.text ? (
                                            <div className="genapps-table__empty-rich">
                                                {empty.icon ? <span className="genapps-table__empty-icon">{empty.icon}</span> : null}
                                                <span className="genapps-table__empty-text">{empty.text}</span>
                                                {empty.action ? <span className="genapps-table__empty-action">{empty.action}</span> : null}
                                            </div>
                                        ) : empty && typeof empty !== 'string' ? (
                                            empty   /* ReactNode custom */
                                        ) : (
                                            (props.emptyText || 'Sem dados para mostrar')
                                        )}
                                    </td></tr>
                                )}
                                {groups ? (
                                    groups.map(function (grp) {
                                        var isCollapsed = collapsedGroups.has(grp.value);
                                        return (
                                            <React.Fragment key={'grp-' + grp.value}>
                                                <tr className="genapps-table__group-header" onClick={function () { toggleGroup(grp.value); }}>
                                                    <td colSpan={visibleColumns.length + leadCols}>
                                                        <span className="genapps-table__group-toggle">{isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}</span>
                                                        <span className="genapps-table__group-label">{grp.value}</span>
                                                        <span className="genapps-table__group-count">{grp.rows.length}</span>
                                                    </td>
                                                </tr>
                                                {!isCollapsed && renderRows(grp.rows)}
                                                {!isCollapsed && totalsEnabled && (
                                                    <TotalsRow rows={grp.rows} orderedColumns={visibleColumns}
                                                        getField={getField} colWidths={colWidths} enableSelection={enableSelection} renderDetail={renderDetail}
                                                        label={subtotalLabel} variant="subtotal" />
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : renderRows(displayRows)}
                                {/* Total geral — so em modo plano (sem grouping); em grouping fica no fim */}
                                {!groups && totalsRowsForFooter && totalShown > 0 && (
                                    <TotalsRow rows={totalsRowsForFooter} orderedColumns={visibleColumns}
                                        getField={getField} colWidths={colWidths} enableSelection={enableSelection} renderDetail={renderDetail}
                                        label={totalsLabel} variant="total" />
                                )}
                                {/* Total geral em modo grouping (depois de todos os grupos) */}
                                {groups && totalsEnabled && totalShown > 0 && (
                                    <TotalsRow rows={filtered} orderedColumns={visibleColumns}
                                        getField={getField} colWidths={colWidths} enableSelection={enableSelection} renderDetail={renderDetail}
                                        label={totalsLabel} variant="total" />
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
            {/* Settings Modal (gear) — F14 densidade + F15 colunas + reset */}
            <SettingsModal
                isOpen={settingsOpen}
                onClose={function () { setSettingsOpen(false); }}
                density={density}
                onDensityChange={handleDensityChange}
                hideableCols={hideableCols}
                hiddenColumns={hiddenColumns}
                onToggleColumn={toggleColumnVisibility}
                onReset={function () { resetLayout(); }}
                totalsEnabled={totalsEnabled}
                onToggleTotals={function () { setTotalsEnabled(!totalsEnabled); }}
                hasAggregates={hasAggregates}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                totalRows={totalShown}
                minRows={minRowsProp}
                hasSort={hasSort}
                onClearSort={handleClearSort}
                hasColFilters={hasColFilters}
                onClearFilters={handleClearFilters}
                hasSearch={hasSearch}
                onClearSearch={handleClearSearch}
                enableSelection={enableSelection}
                hasSelection={hasSelection}
                onClearSelection={handleClearSelection}
                hasGrouping={hasGrouping}
                onClearGrouping={handleClearGrouping}
                persistenceActive={!!onLayoutChange}
            />
            {/* Modal de detalhe (drill-down) — SEMPRE SharedModal (R11). v1.8.1 */}
            {renderDetail && detailRow ? (
                <SharedModal
                    isOpen={true}
                    onClose={function () { setDetailRow(null); }}
                    icon={<Info size={20} />}
                    title={typeof detailTitle === 'function' ? (detailTitle(detailRow) || 'Detalhe') : (detailTitle || 'Detalhe')}
                    subtitle={typeof detailSubtitle === 'function' ? (detailSubtitle(detailRow) || '') : (detailSubtitle || '')}
                    size={detailSize}
                    closeOnEscape={true}
                    footerContent={
                        <button type="button" className="btn-animated-border btn-sm" data-active="true"
                            onClick={function () { setDetailRow(null); }}>
                            <X size={14} /> Fechar
                        </button>
                    }
                >
                    {renderDetail(detailRow)}
                </SharedModal>
            ) : null}
        </div>
    );
}

// ═══ Sub-componentes para a coluna "Ações" (v1.7.2 — standard obrigatório) ═══
// KIS: icon Lucide + tooltip, SEM texto, SEM btn-animated-border, SEM SVG inline inventado.
// Uso:
//   render: (row) => (
//     <DataTable.Actions>
//       <DataTable.Action icon={Eye} label="Ver Detalhes" onClick={() => open(row)} />
//       <DataTable.Action icon={Trash2} label="Eliminar" onClick={() => del(row)} />
//     </DataTable.Actions>
//   )
DataTable.Actions = function (props) {
    return <div className="genapps-table__actions">{props.children}</div>;
};

DataTable.Action = function (props) {
    var Icon = props.icon;                 // componente Lucide, ex: Eye, Pencil, Trash2
    var label = props.label || props.title; // vira tooltip + aria-label
    var size = props.size || 16;
    var cls = 'genapps-table__icon-action' + (props.className ? ' ' + props.className : '') + (props.danger ? ' genapps-table__icon-action--danger' : '');
    var common = {
        className: cls,
        title: label,
        'aria-label': label,
        type: 'button'
    };
    var inner = Icon ? <Icon size={size} /> : null;
    // href (link) em vez de onClick
    if (props.href) {
        return <a href={props.disabled ? undefined : props.href} {...common} style={props.disabled ? { pointerEvents: 'none', opacity: 0.4 } : undefined}>{inner}</a>;
    }
    return (
        <button onClick={props.onClick} disabled={props.disabled || false} {...common}>
            {inner}
        </button>
    );
};

// ═══ DataTable.Badge — status pill standard (v1.8.0) ═══
// KIS: pill uniforme para estados/labels em celulas. Paleta: success/warning/error/info/neutral.
// Uso: <DataTable.Badge status="success">Activo</DataTable.Badge>
DataTable.Badge = function (props) {
    var status = props.status || 'neutral';      // success|warning|error|info|neutral
    var cls = 'genapps-table__badge genapps-table__badge--' + status + (props.className ? ' ' + props.className : '');
    var Icon = props.icon;                        // Lucide icon opcional
    return (
        <span className={cls} title={props.title}>
            {Icon ? <Icon size={12} /> : null}
            {props.children != null ? <span className="genapps-table__badge-text">{props.children}</span> : null}
        </span>
    );
};

export default DataTable;
